import pshLogo from '../../assets/psh-logo.png';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, EyeOff, Mail, Lock, User, Gift, Globe, MapPin, Link as LinkIcon, ShieldCheck } from 'lucide-react';
import { registerUser, loginUser, resetPassword, clearError, setUser } from '../../store/slices/authSlice';
import Button from '../common/Button';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { COUNTRIES } from '../../utils/countries';
import { LANGUAGES, REVERSE_LANGUAGE_MAP, LANGUAGE_MAP } from '../../utils/constants';
import { bindTelegramMiniApp, fetchTelegramBindLink, fetchTelegramBindStatus } from '../../services/telegramBindingService';
import { isTelegramMiniApp, openTelegramBotChat, openTelegramLink } from '../../utils/telegramUtils';

const TELEGRAM_SIGNUP_BIND_KEY = 'psh-signup-telegram-required';

const AuthForm = ({ onTelegramBindCompleted }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { loading, error, isAuthenticated, user } = useSelector((state) => state.auth);

  const [isLogin, setIsLogin] = useState(() => {
    try {
      return sessionStorage.getItem(TELEGRAM_SIGNUP_BIND_KEY) === '1' ? false : true;
    } catch {
      return true;
    }
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    referralCode: '',
    country: '',
    language: REVERSE_LANGUAGE_MAP[i18n.language] || 'English',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });
  const [isSignupBindPending, setIsSignupBindPending] = useState(() => {
    try {
      return sessionStorage.getItem(TELEGRAM_SIGNUP_BIND_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [telegramBindLoading, setTelegramBindLoading] = useState(false);
  const [telegramStatusLoading, setTelegramStatusLoading] = useState(false);
  const [telegramLink, setTelegramLink] = useState('');
  const [telegramBotUsername, setTelegramBotUsername] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [showReferral, setShowReferral] = useState(false);

  // Check for referral code in URL
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const refCode = params.get('ref');
    if (refCode) {
      setFormData(prev => ({ ...prev, referralCode: refCode }));
      setIsLogin(false); // Switch to register mode
      setShowReferral(true); // Show the field
    }
  }, []);

  useEffect(() => {
    if (isSignupBindPending && isAuthenticated && isLogin) {
      setIsLogin(false);
    }
  }, [isSignupBindPending, isAuthenticated, isLogin]);

  useEffect(() => {
    if (isAuthenticated) return;
    if (!isSignupBindPending) return;
    setIsSignupBindPending(false);
    try {
      sessionStorage.removeItem(TELEGRAM_SIGNUP_BIND_KEY);
    } catch {}
  }, [isAuthenticated, isSignupBindPending]);

  useEffect(() => {
    if (isLogin) return;
    if (!isAuthenticated) return;
    if (String(user?.telegramChatId || '').trim()) {
      setIsSignupBindPending(false);
      try {
        sessionStorage.removeItem(TELEGRAM_SIGNUP_BIND_KEY);
      } catch {}
      if (typeof onTelegramBindCompleted === 'function') {
        onTelegramBindCompleted();
      }
      return;
    }
    setIsSignupBindPending(true);
  }, [isLogin, isAuthenticated, user?.telegramChatId, onTelegramBindCompleted]);

  const handleOpenTelegramBind = async () => {
    setTelegramBindLoading(true);
    try {
      if (isTelegramMiniApp()) {
        const bindResult = await bindTelegramMiniApp();
        const nextBotUsername = String(bindResult?.botUsername || telegramBotUsername || '').trim();
        if (nextBotUsername) {
          setTelegramBotUsername(nextBotUsername);
        }
        dispatch(setUser({
          ...(user || {}),
          telegramChatId: bindResult?.chatId || '',
          telegramBoundAt: bindResult?.telegramBoundAt || Date.now(),
          telegramUsername: bindResult?.telegramUsername || user?.telegramUsername || '',
        }));
        setIsSignupBindPending(false);
        try {
          sessionStorage.removeItem(TELEGRAM_SIGNUP_BIND_KEY);
        } catch {}
        if (typeof onTelegramBindCompleted === 'function') {
          onTelegramBindCompleted();
        }
        toast.success(t('auth.telegram_bind_success_signup'));
        if (nextBotUsername) {
          setTimeout(() => {
            openTelegramBotChat(nextBotUsername);
          }, 250);
        }
        return;
      }

      let link = String(telegramLink || '').trim();
      let username = String(telegramBotUsername || '').trim();
      if (!link) {
        const data = await fetchTelegramBindLink();
        link = String(data?.deepLink || '').trim();
        username = String(data?.botUsername || '').trim();
        setTelegramLink(link);
        setTelegramBotUsername(username);
      }
      if (!link) {
        throw new Error(t('auth.telegram_bind_not_ready'));
      }
      openTelegramLink(link);
    } catch (err) {
      toast.error(String(err?.message || t('auth.telegram_bind_failed_open')));
    } finally {
      setTelegramBindLoading(false);
    }
  };

  const handleCheckTelegramStatus = async () => {
    setTelegramStatusLoading(true);
    try {
      const status = await fetchTelegramBindStatus();
      const bound = Boolean(status?.bound);
      if (!bound) {
        toast.error(t('auth.telegram_bind_status_pending'));
        return;
      }

      dispatch(setUser({
        ...(user || {}),
        telegramChatId: status?.chatId || '',
      }));

      setIsSignupBindPending(false);
      try {
        sessionStorage.removeItem(TELEGRAM_SIGNUP_BIND_KEY);
      } catch {}
      if (typeof onTelegramBindCompleted === 'function') {
        onTelegramBindCompleted();
      }
      toast.success(t('auth.telegram_bind_success_signup'));
    } catch (err) {
      toast.error(String(err?.message || 'Failed to check Telegram status.'));
    } finally {
      setTelegramStatusLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    // Auto-change language instantly if the language dropdown was changed
    if (name === 'language') {
      const code = LANGUAGE_MAP[value];
      if (code) {
        i18n.changeLanguage(code);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isLogin) {
      dispatch(loginUser({
        email: formData.email,
        password: formData.password
      }));
    } else {
      if (formData.password !== formData.confirmPassword) {
        toast.error('Passwords do not match.');
        return;
      }
      if (!acceptTerms) {
        toast.error('Please accept the Terms & Conditions to continue.');
        return;
      }
      try {
        const registeredUser = await dispatch(registerUser({
          email: formData.email,
          password: formData.password,
          name: formData.name,
          referralCode: formData.referralCode,
          country: formData.country,
          language: formData.language,
          timezone: formData.timezone,
        })).unwrap();

        if (String(registeredUser?.telegramChatId || '').trim()) {
          try {
            sessionStorage.removeItem(TELEGRAM_SIGNUP_BIND_KEY);
          } catch {}
          setIsSignupBindPending(false);
          if (typeof onTelegramBindCompleted === 'function') {
            onTelegramBindCompleted();
          }
          return;
        }

        if (isTelegramMiniApp()) {
          try {
            const bindResult = await bindTelegramMiniApp();
            const nextBotUsername = String(bindResult?.botUsername || telegramBotUsername || '').trim();
            if (nextBotUsername) {
              setTelegramBotUsername(nextBotUsername);
            }
            dispatch(setUser({
              ...(registeredUser || {}),
              telegramChatId: bindResult?.chatId || '',
              telegramBoundAt: bindResult?.telegramBoundAt || Date.now(),
              telegramUsername: bindResult?.telegramUsername || registeredUser?.telegramUsername || '',
            }));
            setIsSignupBindPending(false);
            try {
              sessionStorage.removeItem(TELEGRAM_SIGNUP_BIND_KEY);
            } catch {}
            if (typeof onTelegramBindCompleted === 'function') {
              onTelegramBindCompleted();
            }
            toast.success(t('auth.telegram_bind_success_signup'));
            if (nextBotUsername) {
              setTimeout(() => {
                openTelegramBotChat(nextBotUsername);
              }, 250);
            }
            return;
          } catch (bindError) {
            toast.error(String(bindError?.message || t('auth.telegram_bind_failed_open')));
          }
        }

        try {
          sessionStorage.setItem(TELEGRAM_SIGNUP_BIND_KEY, '1');
        } catch {}
        setIsSignupBindPending(true);
        toast.error(t('auth.telegram_bind_required_after_signup'));
        toast.success(t('auth.telegram_bind_redirect_after_signup'));
        setTimeout(() => {
          handleOpenTelegramBind();
        }, 120);
      } catch {
        // Error toast is already handled by thunk
      }
    }
  };

  const handleForgotPassword = () => {
    if (!formData.email) {
      toast.error('Please enter your email address first.');
      return;
    }
    dispatch(resetPassword(formData.email));
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-pattern">
      <div className="max-w-md w-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card p-8"
        >
          {/* Header */}
          <div className="text-center mb-8">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2 }}
              className="relative w-16 h-16 rounded-2xl overflow-hidden mx-auto mb-4 shadow-xl ring-2 ring-primary-500/30 flex items-center justify-center bg-slate-950"
            >
              <img src={pshLogo} alt="PSH Store" className="w-full h-full object-cover" />
            </motion.div>
            <h2 className="text-3xl font-bold gradient-text">
              {isLogin ? t('auth.login_title') : t('auth.register_title')}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              {isLogin ? t('auth.login_subtitle') : t('auth.register_subtitle')}
            </p>
            {!isLogin && isSignupBindPending && (
              <div className="mt-4 rounded-xl border border-primary-200 dark:border-primary-700 bg-primary-50 dark:bg-primary-900/30 p-3 text-left">
                <div className="flex items-start gap-2">
                  <ShieldCheck className="w-5 h-5 text-primary-600 dark:text-primary-300 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-primary-700 dark:text-primary-200">{t('auth.telegram_bind_required_title')}</p>
                    <p className="text-xs text-primary-700/90 dark:text-primary-200/90 mt-1">
                      {t('auth.telegram_bind_required_desc')}
                    </p>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={handleOpenTelegramBind}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-xs font-semibold transition-colors disabled:opacity-60"
                    disabled={telegramBindLoading}
                  >
                    <LinkIcon className="w-3.5 h-3.5" />
                    {telegramBindLoading ? t('auth.telegram_linking_opening') : t('auth.telegram_bind_button')}
                  </button>
                  <button
                    type="button"
                    onClick={handleCheckTelegramStatus}
                    className="px-3 py-1.5 rounded-lg border border-primary-300 dark:border-primary-600 text-primary-700 dark:text-primary-200 text-xs font-semibold hover:bg-primary-100 dark:hover:bg-primary-900/50 transition-colors disabled:opacity-60"
                    disabled={telegramStatusLoading}
                  >
                    {telegramStatusLoading ? t('auth.telegram_linking_checking') : t('auth.telegram_check_status_button')}
                  </button>
                </div>
                {telegramBotUsername && (
                  <p className="mt-2 text-[11px] text-primary-700/80 dark:text-primary-200/80">Bot: @{telegramBotUsername}</p>
                )}
              </div>
            )}
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <AnimatePresence mode="wait">
              {!isLogin && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="relative"
                >
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    name="name"
                    placeholder={t('auth.name_label')}
                    value={formData.name}
                    onChange={handleInputChange}
                    required={!isLogin}
                    className="input-primary pl-10"
                  />
                </motion.div>
              )}
            </AnimatePresence>

            {/* Country and Language */}
            <AnimatePresence mode="wait">
              {!isLogin && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="grid grid-cols-2 gap-4"
                >
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
                    <select
                      name="country"
                      value={formData.country}
                      onChange={handleInputChange}
                      required={!isLogin}
                      className="input-primary pl-10 appearance-none bg-white dark:bg-dark-800"
                    >
                      <option value="" disabled>{t('auth.country_label')}</option>
                      {COUNTRIES.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                  <div className="relative">
                    <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
                    <select
                      name="language"
                      value={formData.language}
                      onChange={handleInputChange}
                      required={!isLogin}
                      className="input-primary pl-10 appearance-none bg-white dark:bg-dark-800 notranslate"
                      translate="no"
                    >
                      {LANGUAGES.map((l) => (
                        <option key={l} value={l}>{l}</option>
                      ))}
                    </select>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Email */}
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="email"
                name="email"
                placeholder={t('auth.email_label')}
                value={formData.email}
                onChange={handleInputChange}
                required
                className="input-primary pl-10"
              />
            </div>

            {/* Password */}
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                placeholder={t('auth.password_label')}
                value={formData.password}
                onChange={handleInputChange}
                required
                className="input-primary pl-10 pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>

            {/* Confirm Password */}
            <AnimatePresence mode="wait">
              {!isLogin && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="relative"
                >
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    placeholder={t('auth.confirm_password_label')}
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    required={!isLogin}
                    className="input-primary pl-10 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Have a referral code? (toggle) */}
            {!isLogin && (
              <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
                <span>{t('auth.have_referral')}</span>
                <button
                  type="button"
                  onClick={() => setShowReferral((v) => !v)}
                  className="text-primary-600 dark:text-primary-400 font-medium hover:underline"
                >
                  {showReferral ? t('common.hide') : t('common.add')}
                </button>
              </div>
            )}

            {/* Referral Code */}
            <AnimatePresence mode="wait">
              {!isLogin && showReferral && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="relative"
                >
                  <Gift className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    name="referralCode"
                    placeholder={t('auth.referral_placeholder')}
                    value={formData.referralCode}
                    onChange={(e) => {
                      const v = e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, '');
                      setFormData((prev) => ({ ...prev, referralCode: v }));
                    }}
                    pattern="^PSH-[A-Z]{1,8}-[A-Z0-9]{4}$"
                    title="Format: PSH-USERNAME-AB12 (USERNAME up to 8 letters, last part 4 letters/numbers)"
                    maxLength={20}
                    className="input-primary pl-10"
                  />
                </motion.div>
              )}
            </AnimatePresence>

            {/* Error Message */}
            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800 text-error-700 dark:text-error-400 px-4 py-3 rounded-xl"
                >
                  {error}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Forgot Password */}
            {isLogin && (
              <div className="text-right">
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                >
                  {t('auth.forgot_password')}
                </button>
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              variant="primary"
              size="lg"
              loading={loading}
              className="w-full"
              disabled={!isLogin && isSignupBindPending}
            >
              {isLogin ? t('auth.login_button') : t('auth.register_button')}
            </Button>
          </form>

          {/* Terms and Conditions */}
          {!isLogin && (
            <div className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
              <input
                id="acceptTerms"
                type="checkbox"
                checked={acceptTerms}
                onChange={(e) => setAcceptTerms(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                required
              />
              <label htmlFor="acceptTerms">
                I agree to the{' '}
                <Link to="/terms" className="text-primary-600 dark:text-primary-400 hover:underline">
                  Terms &amp; Conditions
                </Link>{' '}
                and{' '}
                <Link to="/privacy" className="text-primary-600 dark:text-primary-400 hover:underline">
                  Privacy Policy
                </Link>.
              </label>
            </div>
          )}

          {/* Toggle Form */}
          <div className="mt-8 text-center">
            <p className="text-gray-600 dark:text-gray-400">
              {isLogin ? t('auth.no_account') : t('auth.have_account')}
              <button
                onClick={() => {
                  setIsLogin(!isLogin);
                  setAcceptTerms(false);
                  setIsSignupBindPending(false);
                  setTelegramLink('');
                  setTelegramBotUsername('');
                  try {
                    sessionStorage.removeItem(TELEGRAM_SIGNUP_BIND_KEY);
                  } catch {}
                  dispatch(clearError()); // hide incorrect credentials error when switching modes
                }}
                className="ml-2 text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-semibold"
              >
                {isLogin ? t('common.sign_up') : t('common.sign_in')}
              </button>
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AuthForm;
